import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, AlertCircle, Check, MapPin } from "lucide-react";
import {
  useGetExamFinalizeOptionsQuery,
  useFinalizeExamApplicationMutation,
  useInitiateExamPaymentMutation,
  useGetMyExamCallApplicationsQuery,
} from "../../api/examCallsApi";
import { useGetCentersForExamInDietQuery } from "../../api/examsApi";
import { submitOnePayForm } from "../../../logbook/utils/onepay";
import CenterCard from "../../components/CenterCard";
import ExamApplicationFormSections from "../../components/ExamApplicationFormSections";
import ApplicationDocumentsSection from "../../components/ApplicationDocumentsSection";
import CpdShortfallModal from "../../components/CpdShortfallModal";

function safeMsg(err: any) {
  return err?.data?.message || err?.error || err?.message || "Something went wrong";
}

/**
 * Why a load failed, including when it failed while looking like a success.
 *
 * The API can answer 200 with an error body — whitespace ahead of `<?php` in a
 * route file flushes the headers before Laravel sets a status, so a 500 or a
 * 404 arrives as a plain 200. RTK Query files that as data, `application` comes
 * back undefined, and the generic fallback hides the one thing worth reading.
 * Whatever the body said beats "Something went wrong".
 */
function loadError(isError: boolean, err: any, body: any) {
  return isError ? safeMsg(err) : body?.message || safeMsg(err);
}
const paperLabel = (p: any) => p?.title ?? p?.name ?? p?.code ?? p?.id;
const courseLabel = (c: any) => c?.title ?? c?.name ?? c?.code ?? c?.id;

/**
 * Manage a call-based exam application:
 *  - draft / pending_payment (incl. a cancelled payment) → pay / re-pay,
 *  - paid → choose center + papers/courses and submit,
 *  - submitted → change center + papers/courses (until it is graded).
 */
export default function ExamFinalizePage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const { data: options, isLoading, isError, error, refetch } = useGetExamFinalizeOptionsQuery(id, {
    skip: !id,
  });

  const app = options?.application;

  /* ── Which exam this is ───────────────────────────────────────────────────
     Named from the options payload where the server sends it. The applications
     list is the fallback and is already in the cache — every route onto this
     page comes through it — so the heading names the exam on a server that has
     not been deployed with those fields yet, instead of reading "Exam
     application" over somebody's TPC form. */
  const { data: myApps = [] } = useGetMyExamCallApplicationsQuery();

  const listed = useMemo(
    () => (myApps as any[]).find((a) => String(a?.id) === String(id)),
    [myApps, id],
  );

  const examTitle =
    app?.exam_title ?? listed?.call?.exam?.title ?? listed?.exam?.title ?? null;
  const examCode =
    app?.exam_code ?? listed?.call?.exam?.code ?? listed?.exam?.code ?? null;
  const dietTitle =
    app?.diet_title ?? listed?.call?.diet?.title ?? listed?.diet?.title ?? null;
  const callTitle = app?.call_title ?? listed?.call?.title ?? null;

  const isInterview = !!app?.is_interview;
  // Course-based exams (TPC) are neither an interview nor paper-based. Fall back
  // to the old inference for an API that has not been deployed with the flag.
  const isPaperBased =
    app?.is_paper_based ?? (!isInterview && (options?.papers ?? []).length > 0);
  // TPC/PCI candidates complete the full NIQS application form here too.
  const needsForm = !!app?.requires_application_form;
  // The form stays open until the application is submitted.
  const canEditForm = !!app?.can_edit_form;
  // The whole review leg reads the same to the candidate: it is out of their
  // hands, whether the secretariat or the committee currently holds it.
  const isUnderReview = app?.status === "under_review" || app?.status === "with_committee";
  const withCommittee = app?.status === "with_committee";
  const isRejected = app?.status === "rejected";
  const isApproved = app?.status === "approved";
  // Submitted, and waiting on the candidate's own chapter to endorse it —
  // nothing the portal can do for them, but they should know who to ask.
  const awaitingChapter = !!app?.awaiting_chapter;
  // Where a returned application goes back to when they submit it again — the
  // server remembers, so the screen can say rather than guess.
  const returnedFrom = app?.returned_from as string | undefined;
  const reviewNote = app?.review_note as string | undefined;
  const canPay = !!app?.can_pay;
  const canEdit = !!app?.can_edit;
  // Deferred to a later sitting, and the move cancelled the centre they had —
  // so the centre screen is open to them whatever state the application is
  // otherwise in. The server decides this; the page only explains it.
  const owesCentre = !!app?.awaiting_centre_after_deferral;
  const isSubmitted = app?.status === "submitted";
  const papers: any[] = options?.papers ?? [];
  const courses: any[] = options?.courses ?? [];

  /* ── What this exam asks for ──────────────────────────────────────────────
     Both of these are the exam's own decision, sent by the server:

     · Not every exam is a sitting. One decided on the documents alone has
       nowhere to turn up, so it asks for no centre — putting a centre picker in
       front of that candidate is a question with no true answer.
     · The hand-in (O'level, certificates, reports) is whatever the exam's
       `requires_*` flags ask for, which may be nothing at all.

     What is still outstanding comes from the server too — it is the same list
     finalize() refuses on, so the Submit button and the refusal cannot drift
     apart. Defaulted to true for an API not yet deployed with the flag, so a
     stale server keeps today's behaviour instead of losing the centre step. */
  const needsCentre = app?.requires_exam_centre ?? true;
  const needsDocuments = !!app?.requires_documents;

  /* Courses are registered only where the exam says so. A course list that
     exists for the graders is not something the candidate was ever asked to
     choose from, and the server refuses a submission without one where it IS
     asked. Defaulted from the presence of courses for an API not yet deployed
     with the flag, which is how this behaved before. */
  const needsCourses = app?.requires_course_selection ?? (options?.courses ?? []).length > 0;
  const [requirementsMissing, setRequirementsMissing] = useState<string[] | null>(null);

  // Memoised because `blockers` below depends on it: a fresh `[]` on every
  // render would make that memo recompute every time and defeat itself.
  const outstanding = useMemo<string[]>(
    () => requirementsMissing ?? app?.requirements_missing ?? [],
    [requirementsMissing, app?.requirements_missing],
  );

  // Re-reading the options after a save would overwrite a fresher local list,
  // so the local one is dropped whenever the server's answer changes.
  useEffect(() => {
    setRequirementsMissing(null);
  }, [app?.requirements_missing]);

  const { data: centersData, isFetching: loadingCenters } = useGetCentersForExamInDietQuery(
    { dietId: app?.exam_diet_id, examId: app?.exam_id },
    { skip: !app?.exam_diet_id || !app?.exam_id || !canEdit || !needsCentre },
  );
  const centers: any[] = centersData?.centers ?? [];

  const [centerId, setCenterId] = useState("");
  const [paperIds, setPaperIds] = useState<string[]>([]);
  const [courseIds, setCourseIds] = useState<string[]>([]);
  const [banner, setBanner] = useState<string | null>(null);
  // Populated when the backend rejects a submit because the form is short.
  const [formMissing, setFormMissing] = useState<string[]>([]);

  // Pre-fill from the current selections; default papers to all outstanding.
  useEffect(() => {
    if (!app) return;
    setCenterId(app.exam_center_id ?? "");
    const selectedPapers: string[] = app.selected_paper_ids ?? [];
    setPaperIds(selectedPapers.length ? selectedPapers : papers.map((p) => p.id));
    setCourseIds(app.selected_course_ids ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app?.id, app?.status, papers.length]);

  const [finalize, { isLoading: submitting }] = useFinalizeExamApplicationMutation();
  const [initiatePay, { isLoading: paying }] = useInitiateExamPaymentMutation();

  /* ── The CPD bar, when this exam sets one ────────────────────────────────
     The refusal carries the standing it refused on, which is fresher than
     anything the page holds, so it is preferred over the options payload. */
  const [cpdOpen, setCpdOpen] = useState(false);
  const [cpdInfo, setCpdInfo] = useState<any>(null);
  const [cpdReason, setCpdReason] = useState<string | null>(null);
  const [cpdCanAttest, setCpdCanAttest] = useState(true);

  // Sent unpaid as well as paid, so the shortfall can be shown before the fee
  // is attempted. Absent on an API deployed before this — nothing is claimed
  // about the standing when the server did not send one.
  const cpdRequired = Number(app?.cpd?.required ?? 0);
  const cpdShort =
    cpdRequired > 0 &&
    !app?.cpd?.attested_at &&
    Number(app?.cpd?.effective ?? 0) < cpdRequired;

  const openCpdIfShort = (payload: any): boolean => {
    const d = payload?.code ? payload : payload?.data;
    if (d?.code !== "CPD_SHORT") return false;

    setCpdInfo(d.cpd ?? app?.cpd ?? null);
    setCpdReason(d.message ?? null);
    // Defaulted to true for an API deployed before the declaration existed:
    // the worst case is a checkbox the server ignores, against a dead end.
    setCpdCanAttest(d.can_attest ?? true);
    setCpdOpen(true);
    return true;
  };

  const toggle = (list: string[], set: (v: string[]) => void, v: string) =>
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  // A paper-based exam needs at least one paper picked. An exam that is not a
  // sitting needs no centre. Everything else this exam asks for — documents, a
  // graduation year, a CPD confirmation — has to be in before it can go.
  /**
   * Everything standing between this application and submission, in one list.
   *
   * The server's `outstanding` covers what it can see — documents, CPD, the
   * declared graduation year. It cannot report the centre or the papers,
   * because those are choices still sitting in this page's state and have
   * never been sent. Joining them here means the candidate reads ONE list
   * rather than discovering the centre separately from a greyed-out button.
   */
  const blockers = useMemo(() => {
    const list = [...outstanding];
    if (needsCentre && !centerId) list.push("Choose your exam centre.");
    if (isPaperBased && paperIds.length === 0) list.push("Select at least one paper to sit.");
    if (needsCourses && courseIds.length === 0) list.push("Register at least one course.");
    return list;
  }, [outstanding, needsCentre, centerId, isPaperBased, paperIds.length, needsCourses, courseIds.length]);

  const canSubmit = blockers.length === 0 && !submitting;

  /**
   * Pay the fee — and, where this exam sets a CPD bar the candidate is short
   * of, offer the two ways past it instead of a red message.
   *
   * The bar is enforced before payment on purpose, so nobody buys a sitting
   * they are ruled out of. But a refusal with nothing to do next stranded
   * candidates who WERE qualified: points earned at another body's event sit on
   * a certificate long before they reach the ledger. So the refusal opens the
   * shortfall modal, where they can claim the points (a claim counts straight
   * away) or declare that they hold them — and the retry goes back through the
   * same endpoint, which re-reads the record and decides. Nothing here assumes
   * either was enough.
   */
  const handlePay = async (cpdAttested = false) => {
    setBanner(null);
    try {
      const res: any = await initiatePay(
        cpdAttested ? { id, cpd_attested: true } : id,
      ).unwrap();

      // Some blockers come back on a 2xx, so the body is checked either way.
      if (openCpdIfShort(res)) return;

      if (res?.already_paid) {
        setCpdOpen(false);
        await refetch();
        return;
      }
      if (!res?.payment_url) throw new Error(res?.message || "Could not start the payment.");
      setCpdOpen(false);
      submitOnePayForm(res);
    } catch (e: any) {
      if (openCpdIfShort(e?.data)) return;
      setBanner(safeMsg(e));
    }
  };

  const onSubmit = async () => {
    setBanner(null);
    try {
      await finalize({
        id,
        // Omitted entirely for an exam that is not a sitting — sending an empty
        // string would fail the uuid rule rather than read as "no centre".
        ...(needsCentre ? { exam_center_id: centerId } : {}),
        exam_paper_ids: paperIds,
        exam_course_ids: courseIds,
      }).unwrap();


      navigate("/exams/calls/applications", { replace: true });
    } catch (e: any) {
      setFormMissing(Array.isArray(e?.data?.missing) ? e.data.missing : []);
      setBanner(safeMsg(e));
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-gray-600 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (isError || !app) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-rose-700">
        {loadError(isError, error, options)}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-5">
      <div className="bg-white border rounded-xl p-5">
        <Link to="/exams/calls/applications" className="text-sm text-gray-600 hover:underline">
          ← My Applications
        </Link>

        {/* WHICH application is being filled. The heading below says what to do
            with it, which is not the same question — and a candidate holding
            applications to two exams cannot tell these pages apart from the
            URL. Falls back quietly on an API deployed before this was sent. */}
        <div className="mt-3 flex flex-wrap items-baseline gap-x-2">
          {examCode ? (
            <span className="text-[11px] font-black uppercase tracking-widest text-indigo-600">
              {examCode}
            </span>
          ) : null}
          <span className="text-base font-semibold text-gray-900">
            {examTitle ?? "Exam application"}
          </span>
        </div>
        {[dietTitle, callTitle].filter(Boolean).length ? (
          <p className="text-xs text-gray-500 mt-0.5">
            {[dietTitle, callTitle].filter(Boolean).join(" · ")}
          </p>
        ) : null}

        <h1 className="text-xl font-bold text-gray-900 mt-3">
          {canPay
            ? "Complete your application"
            : isUnderReview
              ? "Your application is under review"
              : isApproved
                ? (isInterview
                    ? "Approved for interview"
                    : "Approved to sit the exam")
                : isSubmitted
                ? "Manage your application"
                : "Finish your application"}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {canPay
            ? "Your payment hasn't been completed. Pay the application fee to continue."
            : isUnderReview
              ? (withCommittee
                  ? "The Membership Committee is considering your application. There is nothing for you to do right now."
                  : "The secretariat is reviewing your application. There is nothing for you to do right now.")
              : isApproved
                ? "Nothing further is needed from you right now."
                : isSubmitted
                ? needsCentre
                  ? "You can change your center or papers until the exam is graded."
                  : "You can change your application until the exam is graded."
                : needsForm
                  ? "Complete your application form, then send it for review."
                  // What is left to do depends on the exam: a sitting needs a
                  // centre, an exam decided on the documents alone does not.
                  : needsCentre
                    ? "Payment confirmed. Choose your exam center and the papers you are registering for."
                    : needsDocuments
                      ? "Payment confirmed. Upload the documents this exam asks for, then submit — there is no exam centre to choose."
                      : "Payment confirmed. Submit your application to complete it."}
        </p>

        {banner ? (
          <div className="mt-4 text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded-lg p-3">
            {banner}
            {formMissing.length > 0 ? (
              <ul className="mt-1.5 list-disc list-inside space-y-0.5">
                {formMissing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Unpaid / cancelled payment → pay or re-pay */}
      {canPay ? (
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="text-sm">
              This application is awaiting payment{" "}
              {app.status === "pending_payment" ? "(your last payment wasn't completed)" : ""}.
            </div>
          </div>
          {/* Said before the button is pressed, not only in the refusal that
              comes back from pressing it. The fee is refused on this bar, and a
              candidate who can see the shortfall coming can deal with it in the
              same sitting. */}
          {cpdShort ? (
            <div className="mt-3 flex flex-wrap items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-900">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                This exam requires {cpdRequired} CPD points and your record counts{" "}
                {Number(app.cpd?.effective ?? 0)}. Claim the CPD you have earned, or confirm you
                hold it, to continue.
              </div>
              <button
                onClick={() => {
                  setCpdInfo(app.cpd ?? null);
                  setCpdReason(null);
                  setCpdOpen(true);
                }}
                className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-50"
              >
                Sort out CPD
              </button>
            </div>
          ) : null}

          <div className="mt-4 flex justify-end">
            <button
              onClick={() => handlePay()}
              disabled={paying}
              className="px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800 disabled:opacity-50"
            >
              {paying ? "Starting payment…" : "Pay application fee"}
            </button>
          </div>
        </div>
      ) : null}

      {/* Carried over to a later sitting, and owing a centre for it. Shown
          above every other banner because it is the one thing the candidate has
          to act on — the deferral cancelled the centre they had chosen. */}
      {owesCentre ? (
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Deferred — set up your new sitting</p>
              <p className="mt-0.5">
                Your application has been carried over to a later sitting, and your fee comes with
                it. The centre you picked before was for the earlier one, so choose again below —
                along with anything else this exam asks you to register — and submit. Your exam
                number is issued once you have chosen, and appears on your admission card.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Submitted, waiting on the candidate's own chapter */}
      {awaitingChapter ? (
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Waiting on your chapter</p>
              <p className="mt-0.5">
                Your application is in. Your chapter chairman or secretary now endorses it, and it
                goes to the secretariat from there. Approach your chapter if it sits here for long.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* With the secretariat, or with the committee */}
      {isUnderReview ? (
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-start gap-2 text-sky-800 bg-sky-50 border border-sky-100 rounded-lg p-3 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">
                {withCommittee ? "With the Membership Committee" : "With the secretariat"}
              </p>
              <p className="mt-0.5">
                Your chapter has endorsed this application and it can't be changed while it is being
                reviewed. {withCommittee
                  ? "A committee member decides it from here."
                  : "The secretariat passes it to the Membership Committee, who decide it."}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Approved — the end of the road for an exam application */}
      {isApproved ? (
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-start gap-2 text-green-800 bg-green-50 border border-green-100 rounded-lg p-3 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              {/* An interview is a panel appointment and an exam is a sitting;
                  "approved" means a different next thing for each, so it says
                  which rather than leaving the candidate to infer it. */}
              <p className="font-semibold">
                {isInterview ? "Approved for interview" : "Approved to sit the exam"}
              </p>
              <p className="mt-0.5">
                {isInterview
                  ? "You have been approved for the interview."
                  : "You have been approved to sit this exam."}{" "}
                Your admission card and timetable will be ready shortly, from your applications
                list.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Returned or turned down — show why */}
      {reviewNote && !isUnderReview ? (
        <div
          className={`bg-white border rounded-xl p-5 text-sm ${isRejected ? "text-rose-800" : "text-amber-800"}`}
        >
          <p className="font-semibold">
            {isRejected ? "Your application was not approved" : "Your application was returned for correction"}
          </p>
          <p className="mt-1 whitespace-pre-line">{reviewNote}</p>
          {returnedFrom && !isRejected ? (
            <p className="mt-2 text-gray-600">
              Correct it and submit again — it goes straight back to{" "}
              {returnedFrom === "with_committee"
                ? "the Membership Committee"
                : returnedFrom === "under_review"
                  ? "the secretariat"
                  : "your chapter for endorsement"}
              , so you do not start over.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* The application form — fill it before sending for review */}
      {needsForm && (canEditForm || isUnderReview) ? (
        <>
          <ExamApplicationFormSections
            applicationId={id}
            readOnly={!canEditForm}
            onSaved={(complete) => {
              if (complete) setFormMissing([]);
            }}
          />

        </>
      ) : null}

      {/* Cannot pay, cannot edit the form, cannot pick a centre → nothing to do */}
      {!canPay && !canEdit && !canEditForm && !isUnderReview && !isApproved ? (
        <div className="bg-white border rounded-xl p-5 text-sm text-gray-600">
          This application can no longer be changed (its status is <b>{app.status}</b>).
        </div>
      ) : null}

      {/* Paid or submitted → center + papers/courses */}
      {canEdit ? (
        <>
          {/* The hand-in, above the centre because it is the bulk of the work —
              a candidate who picks a centre first and then finds a locked
              Submit button has been led the wrong way round. */}
          {needsDocuments ? (
            <ApplicationDocumentsSection
              applicationId={id}
              // Both screens now ask for O'level the same way — by sitting,
              // with its scanned slip — over the same rows. Only one of them
              // may be on the page at a time: two editors over one table is
              // how a sitting loses its exam number and its upload. Where the
              // full TPC/PCI form is shown, section 3.0 of THAT form is the
              // O'level editor, and this section leaves it alone.
              showOlevels={!needsForm}
              onSaved={setRequirementsMissing}
            />
          ) : null}

          {/* Centre — only for an exam that is actually sat somewhere.
              Deliberately loud while nothing is chosen: it is required, it is
              the one decision on this page that cannot be inferred, and a
              candidate who scrolls past it meets a disabled Submit button with
              no idea why. The whole card carries the state, not just the
              cards inside it. */}
          {needsCentre ? (
            <div
              className={`bg-white border-2 rounded-xl p-5 ${
                centerId ? "border-gray-200" : "border-amber-300 ring-4 ring-amber-100"
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div
                    className={`shrink-0 p-2 rounded-xl ${
                      centerId ? "bg-emerald-50 text-emerald-600" : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 flex flex-wrap items-center gap-2">
                      Choose your exam centre
                      <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                        Required
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      Where you will sit this exam. You can change it until the exam is graded.
                    </div>
                  </div>
                </div>

                {/* What is currently chosen, repeated at the top so it is
                    readable without scanning the whole list again. */}
                {centerId ? (
                  <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
                    <Check className="w-3.5 h-3.5" />
                    {centers.find((s: any) => s.center.id === centerId)?.center?.name ?? "Chosen"}
                  </span>
                ) : null}
              </div>

              {!centerId && centers.length > 0 ? (
                <div className="mt-4 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    You have not picked a centre yet. Choose one below — you cannot submit
                    without it.
                  </span>
                </div>
              ) : null}

              <div className="mt-4">
                {loadingCenters ? (
                  <div className="text-sm text-gray-600">Loading centres…</div>
                ) : centers.length === 0 ? (
                  <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    No centres have been enabled for this exam in this diet yet. Please check
                    back, or contact the secretariat.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {centers.map((s: any) => (
                      <CenterCard
                        key={s.center.id}
                        name={s.center.name}
                        state={s.center.state}
                        address={s.center.address}
                        capacity={s.capacity ?? s.center.capacity ?? null}
                        selected={centerId === s.center.id}
                        onClick={() => setCenterId(s.center.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Papers — interview and course-based exams have none to show */}
          {isPaperBased ? (
            <div className="bg-white border rounded-xl p-5">
              <div className="font-semibold text-gray-900">Papers</div>
              <div className="text-sm text-gray-500 mb-3">
                These are the papers you still need to pass. Uncheck any you don't want to sit this diet.
              </div>
              <div className="space-y-2">
                {papers.map((p) => (
                  <label
                    key={p.id}
                    className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                      paperIds.includes(p.id) ? "bg-gray-50" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={paperIds.includes(p.id)}
                      onChange={() => toggle(paperIds, setPaperIds, p.id)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm font-medium text-gray-800">{paperLabel(p)}</span>
                  </label>
                ))}
                {!papers.length ? (
                  <div className="text-sm text-gray-600">No outstanding papers found.</div>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Courses — only where the exam actually registers them. */}
          {/* Rendered whenever the exam registers courses — including when the
              list is empty, which is a state the candidate has to be able to
              SEE. Hiding the section would leave Submit blocked on "register at
              least one course" with nothing on screen to register. */}
          {needsCourses ? (
            <div
              className={`bg-white border-2 rounded-xl p-5 ${
                courseIds.length ? "border-gray-200" : "border-amber-300 ring-4 ring-amber-100"
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-semibold text-gray-900">Courses</div>
                <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                  Required
                </span>
              </div>
              <div className="text-sm text-gray-500 mb-3">
                Register the courses you are taking. At least one is required.
              </div>

              {courses.length === 0 ? (
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  No courses are currently open for this exam, so there is nothing to register
                  yet. Please contact the secretariat — your application cannot be submitted
                  until courses are available.
                </div>
              ) : null}

              <div className="space-y-2">
                {courses.map((c) => (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                      courseIds.includes(c.id) ? "bg-gray-50" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={courseIds.includes(c.id)}
                      onChange={() => toggle(courseIds, setCourseIds, c.id)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm font-medium text-gray-800">{courseLabel(c)}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          {/* Submitting is a distinct act from saving your documents, and it is
              what moves the application to `submitted`. Given its own panel so
              it does not read as one more button among the section saves. */}
          <div className="bg-white border rounded-xl p-5 space-y-3">
            {/* Everything standing in the way, in one list — the server's
                outstanding requirements plus the centre, which the server
                cannot report because it has not been sent one yet. */}
            {blockers.length > 0 ? (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="font-semibold">
                  {blockers.length} thing{blockers.length === 1 ? "" : "s"} to finish before you
                  can submit
                </p>
                <ul className="mt-1 list-disc list-inside space-y-0.5">
                  {blockers.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                {isSubmitted
                  ? "Your application is in. You can still change it until the exam is graded."
                  : "Everything is in. Submitting sends your application to the secretariat."}
              </p>
            )}

            <div className="flex justify-end">
              <button
                disabled={!canSubmit}
                onClick={onSubmit}
                title={blockers[0] ?? undefined}
                className="px-6 py-3 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50"
              >
                {submitting
                  ? "Submitting…"
                  : isSubmitted
                    ? "Update application"
                    : "Submit application"}
              </button>
            </div>
          </div>
        </>
      ) : null}

      {/* The CPD bar, with both ways past it. Retry goes back through the same
          payment endpoint, carrying the declaration if one was made. */}
      <CpdShortfallModal
        open={cpdOpen}
        onClose={() => setCpdOpen(false)}
        cpd={cpdInfo}
        reason={cpdReason}
        examTitle={examTitle ?? undefined}
        canAttest={cpdCanAttest}
        onRetry={(attested) => handlePay(attested)}
        retrying={paying}
      />
    </div>
  );
}
